import { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import './PagamentoMensalidadeModal.css';

const PROVIDERS = [
  {
    id: 'pix',
    icon: 'fas fa-qrcode',
    title: 'Pix',
    desc: 'Banco brasileiro — confirmação automática',
    badge: 'BRL',
  },
  {
    id: 'cartao',
    icon: 'fas fa-credit-card',
    title: 'Cartão (Mercado Pago)',
    desc: 'Cartão emitido no Brasil',
    badge: 'BRL',
  },
  {
    id: 'paypal',
    icon: 'fab fa-paypal',
    title: 'PayPal',
    desc: 'Conta ou cartão internacional',
    badge: 'EUR',
  },
  {
    id: 'stripe',
    icon: 'fas fa-euro-sign',
    title: 'Cartão Europeu (Stripe)',
    desc: 'Cartão emitido na Europa',
    badge: 'EUR',
  },
];

export default function PagamentoMensalidadeModal({ onClose, onPaid }) {
  const [config,      setConfig]      = useState(null);
  const [step,        setStep]        = useState('choose'); // choose | pix | loading
  const [error,       setError]       = useState(null);
  const [pix,         setPix]         = useState(null);
  const [pixStatus,   setPixStatus]   = useState('pending');
  const [copied,      setCopied]      = useState(false);

  const pollRef = useRef(null);

  useEffect(() => {
    api.get('/pagamentos/config').then(r => setConfig(r.data)).catch(() => setConfig({}));
    return () => clearInterval(pollRef.current);
  }, []);

  async function escolherProvider(id) {
    setError(null);

    if (id === 'pix') {
      setStep('loading');
      try {
        const { data } = await api.post('/pagamentos/mensalidade/pix');
        setPix(data);
        setStep('pix');
        pollRef.current = setInterval(async () => {
          try {
            const { data: statusData } = await api.get(`/pagamentos/mensalidade/pix/status/${data.paymentId}`);
            setPixStatus(statusData.status);
            if (statusData.status === 'approved') {
              clearInterval(pollRef.current);
              setTimeout(() => onPaid(), 1500);
            }
          } catch { /* silencia erros de polling */ }
        }, 5000);
      } catch (e) {
        setError('Erro ao gerar Pix. Tente novamente.');
        setStep('choose');
      }
      return;
    }

    if (id === 'cartao') {
      setStep('loading');
      try {
        const { data } = await api.post('/pagamentos/mensalidade/cartao');
        window.location.href = data.init_point;
      } catch {
        setError('Erro ao iniciar pagamento com cartão.');
        setStep('choose');
      }
      return;
    }

    if (id === 'paypal') {
      setStep('loading');
      try {
        const { data } = await api.post('/pagamentos/mensalidade/paypal');
        window.location.href = data.approvalUrl;
      } catch {
        setError('Erro ao iniciar pagamento PayPal.');
        setStep('choose');
      }
      return;
    }

    if (id === 'stripe') {
      setStep('loading');
      try {
        const { data } = await api.post('/pagamentos/mensalidade/stripe');
        window.location.href = data.url;
      } catch {
        setError('Erro ao iniciar pagamento Stripe. Verifique se já foi configurado.');
        setStep('choose');
      }
      return;
    }
  }

  function copiarCodigo() {
    if (!pix?.pixCopyPaste) return;
    navigator.clipboard.writeText(pix.pixCopyPaste).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  }

  return (
    <div className="pmm-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="pmm-modal">
        <button className="pmm-close" onClick={onClose} aria-label="Fechar"><i className="fas fa-times" /></button>

        {step === 'choose' && (
          <>
            <h2 className="pmm-title"><i className="fas fa-wallet" /> Pagar Mensalidade</h2>
            <p className="pmm-subtitle">Escolha a forma de pagamento mais conveniente para você.</p>

            {error && <div className="pmm-error">{error}</div>}

            <div className="pmm-providers">
              {PROVIDERS.filter(p => p.id !== 'stripe' && p.id !== 'paypal').map(p => (
                <button
                  key={p.id}
                  className="pmm-provider-btn"
                  onClick={() => escolherProvider(p.id)}
                  disabled={!config}
                >
                  <i className={p.icon} />
                  <div className="pmm-provider-info">
                    <div className="pmm-provider-title">{p.title}</div>
                    <div className="pmm-provider-desc">{p.desc}</div>
                  </div>
                  <span className="pmm-provider-badge">{p.badge}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {step === 'loading' && (
          <div className="pmm-loading">
            <i className="fas fa-spinner fa-spin" />
            <p>Preparando pagamento...</p>
          </div>
        )}

        {step === 'pix' && pix && (
          <div className="pmm-pix">
            <h2 className="pmm-title"><i className="fas fa-qrcode" /> Pague via Pix</h2>

            {pixStatus === 'approved' && (
              <div className="pmm-status pmm-status-ok">
                <i className="fas fa-check-circle" /> Pagamento confirmado!
              </div>
            )}
            {pixStatus === 'pending' && (
              <div className="pmm-status pmm-status-pending">
                <i className="fas fa-clock" /> Aguardando pagamento — verificação automática
              </div>
            )}
            {pixStatus === 'rejected' && (
              <div className="pmm-status pmm-status-error">
                <i className="fas fa-times-circle" /> Pagamento recusado.
              </div>
            )}

            {pix.qrCode && <img src={pix.qrCode} alt="QR Code Pix" className="pmm-qr" />}

            {pix.pixCopyPaste && (
              <>
                <div className="pmm-code">{pix.pixCopyPaste}</div>
                <button className="pmm-btn-copy" onClick={copiarCodigo}>
                  {copied ? 'Copiado!' : 'Copiar código Pix'}
                </button>
              </>
            )}

            <p className="pmm-hint">O QR Code expira em 30 minutos. Após o pagamento, a confirmação é automática.</p>
          </div>
        )}
      </div>
    </div>
  );
}
